/**
 * Page controller.
 *
 * The responsibility of this module is to handle switching between pages.
 *
 * @module app/controllers/PageController
 */
/* eslint-disable require-jsdoc */
import _ from 'lodash';
import * as Routing from './Routing';
import BasePage from '../pages/BasePage';
import MessageBusTopics from '../../common/MessageBusTopics';
import moment from 'moment-timezone';
import pages from '../pages/Pages';
import SportsDataRepository from '../components/SportsDataRepository';
import { isClientFeatureEnabled } from '@wh/feature-toggle-detector';

window.Routing = Routing;

const getLocationPathname = () => window.Routing.history.location.pathname;

const cleanUpTestingParameters = arg => (arg && arg.indexOf('showTranslationsTip') < 0) ? arg : undefined;

let currentPage = null;
let deps = null;

const changePage = async function (getNewPage, parameters) {
    if (currentPage) {
        BasePage.unload(currentPage);
    }

    const newPage = await getNewPage();

    if(!currentPage) { // set initial page as pre-rendered
        newPage.setRendered();
    }
    currentPage = newPage;
    window.WH.messageBus.publishSticky(MessageBusTopics.CURRENT_PAGE, currentPage);
    BasePage.display(currentPage, parameters, { ...deps, sportsDataRepository: SportsDataRepository });
};

const fallbackPage = {
    fallback: {
        paths: [
            'betting/:locale/:sport/:class',
            'betting/:locale/:sport/:class/:subclass',
        ],
        action: (locale, sport, sportclass, subclass) => {
            changePage(pages.getFallback, {
                locale,
                sport,
                sportclass,
                subclass,
                page: `${sport}-${sportclass}`,
            });
        },
    },
};

const competitionsPage = {
    competitions: {
        paths: [
            'betting/:locale/:sport/competitions',
            'betting/:locale/:sport/competitions(/:competitionName)(/:filter)',
        ],
        action: (locale, sport, competitionName, filter) => {
            if (window.isCompetitionsMS && typeof window.WH.sportsbook.from.page !== 'undefined') {
                return;
            }

            if (/matches|outrights/.test(competitionName) && !filter) {
                filter = competitionName;
                competitionName = undefined;
            }

            changePage(pages.getCompetitions, {
                locale: locale,
                sport: sport,
                competitionName: competitionName,
                filter: cleanUpTestingParameters(filter),
                page: 'competitions',
            });
        },
    },

    footballCompetitionMatches: {
        paths: [
            'betting/:locale/football/competitions/OB_TY:competitionId/:compName/:filter(/:filterId)(/:filterName)',
        ],
        action: (locale, competitionId, compName, filter, filterId, filterName) => {
            if (window.isCompetitionsMS && typeof window.WH.sportsbook.from.page !== 'undefined') {
                return;
            }

            changePage(pages.getCompetitions, {
                locale: locale,
                sport: 'football',
                competitionId: 'OB_TY' + competitionId,
                competitionName: compName,
                filter: cleanUpTestingParameters(filter),
                page: 'competitions',
                filterId: filterId,
                filterName: filterName,
            });
        },
    },

    competitionById: {
        paths: [
            'betting/:locale/:sport/OB_TY:competionId(/:competitionName)',
        ],
        action: function (locale, sport, competitionId) {
            competitionId = cleanUpTestingParameters(competitionId);

            changePage(pages.getCompetitions, {
                locale: locale,
                sport: sport,
                competitionId: 'OB_TY' + competitionId,
                page: 'competitions',
            });
        },
    },

    competitionByFilterAndId: {
        paths: [
            'betting/:locale/:sport/competitions/:filter/OB_TY:competionId(/:competitionName)',
        ],
        action: function (locale, sport, filter, competitionId) {
            filter = cleanUpTestingParameters(filter);
            competitionId = cleanUpTestingParameters(competitionId);

            changePage(pages.getCompetitions, {
                locale: locale,
                sport: sport,
                filter: filter,
                competitionId: 'OB_TY' + competitionId,
                page: 'competitions',
            });
        },
    },
};

const meetingsPage = {
    meetingsMarkets: {
        paths: [
            'betting/:locale/:sport/meetings/markets/OB_EV:eventId(/:eventName)',
        ],
        action: function (locale, sport, meetingMarketsId) {
            meetingMarketsId = cleanUpTestingParameters(meetingMarketsId);
            changePage(pages.getMeetings, {
                locale: locale,
                sport: sport,
                meetingMarketsId: 'OB_EV' + meetingMarketsId,
                page: 'meetings',
            });
        },
    },

    meetingsAllRaces: {
        paths: [
            'betting/:locale/:sport/meetings/:day/OB_TY:competitionId(/:competitionName)',
        ],
        action: function (locale, sport, day, competitionId) {
            day = cleanUpTestingParameters(day);
            competitionId = cleanUpTestingParameters(competitionId);

            changePage(pages.getMeetings, {
                locale: locale,
                sport: sport,
                day: day,
                competitionId: 'OB_TY' + competitionId,
                page: 'meetings',
            });
        },
    },
};

const isRacingPage = sport => currentPage
    && currentPage.id === 'racing'
    && currentPage.args && currentPage.args.sport === sport;

const racingPage = {
    racing: {
        paths: [
            'betting/:locale/:sport/meetings',
            'betting/:locale/:sport/meetings/:regionFilter(/:dayFilter)',
        ],
        action: (locale, sport, regionFilter, dayFilter) => {
            const path = ['meetings', regionFilter, dayFilter].filter(v => v).join('/');
            const comingFromSideNavigation = !regionFilter && !dayFilter;

            if (!isRacingPage(sport) || comingFromSideNavigation) {
                changePage(pages.getRacing, { page: 'racing', locale, sport, path });
            }
        },
    },
    racingRacecard: {
        paths: [
            'betting/:locale/:sport/racecard/:raceId(/:raceSlug)',
        ],
        action: (locale, sport, raceId, raceSlug) => {
            const path = ['racecard', raceId, raceSlug].filter(v => v).join('/');

            if (!isRacingPage(sport)) {
                changePage(pages.getRacing, { page: 'racing', locale, sport, path });
            }
        },
    },
    racingAllRaces: {
        paths: [
            'betting/:locale/:sport/all-racecards/:competitionId/:day',
        ],
        action: (locale, sport, competitionId, day) => {
            const path = ['all-racecards', competitionId, day].filter(v => v).join('/');

            if (!isRacingPage(sport)) {
                changePage(pages.getRacing, { page: 'racing', locale, sport, path });
            }
        },
    },
};

const isMatchesPage = sport => currentPage && currentPage.id === 'matches' && currentPage.args.sport === sport;

const isDailyMatchListMSActivated = sportName => {
    const isFeatureToggleEnabled = isClientFeatureEnabled('dailyMatchListEnabled', 'internal')
        || isClientFeatureEnabled('dailyMatchListEnabled', 'public');
    const dmlConfig = window.WH.sportsbook.frontEndConfig.dailyMatchList;

    const isActivated = dmlConfig.activatedSports.includes(sportName)
        && (!dmlConfig.hiddenLive || isFeatureToggleEnabled);

    return isActivated;
};

const matchesPage = {
    matches: {
        paths: [
            'betting/:locale/:sport/matches',
            'betting/:locale/:sport/matches/:sortType',
            'betting/:locale/:sport/matches/:sortType/:filter',
            'betting/:locale/:sport/matches/:sortType/:filter/:marketGroupName',
        ],
        action: (locale, sport, sortType, filter, marketGroupName) => {
            sortType = (sortType && sortType.indexOf('=') < 0) ? cleanUpTestingParameters(sortType) : undefined;
            filter = cleanUpTestingParameters(filter);
            marketGroupName = cleanUpTestingParameters(marketGroupName);

            const isActivated = isDailyMatchListMSActivated(sport);

            const disableRouting = isActivated && isMatchesPage(sport);
            if (disableRouting) {
                return;
            }

            const destinationPage = isActivated
                ? pages.getDailyMatchList
                : pages.getMatches;

            changePage(destinationPage, {
                locale: locale,
                sport: sport,
                sortType: sortType,
                filter: filter,
                marketGroupName: marketGroupName,
                page: 'matches',
            });
        },
    },
};

const antePostPage = {
    antePost: {
        paths: [
            'betting/:locale/:sport/future-races',
        ],
        action: function (locale, sport) {
            changePage(pages.getAntePost, {
                locale: locale,
                sport: sport,
                page: 'antePost',
            });
        },
    },

    antePostAllRaces: {
        paths: [
            'betting/:locale/:sport/future-races/OB_TY:competitionId(/:competitionName)',
        ],
        action: function (locale, sport, competitionId) {
            competitionId = cleanUpTestingParameters(competitionId);

            changePage(pages.getAntePost, {
                locale: locale,
                sport: sport,
                competitionId: 'OB_TY' + competitionId,
                page: 'antePost',
            });
        },
    },
};

const specialsPage = {
    specials: {
        paths: [
            'betting/:locale/:sport/specials',
            'betting/:locale/:sport/specials/specialsMarkets/OB_EV:eventId(/:eventName)',
        ],
        action: (locale, sport, raceId) => {
            changePage(pages.getSpecials, {
                locale: locale,
                sport: sport,
                raceId: raceId,
                page: 'specials',
            });
        },
    },
};

const virtualPage = {
    virtual: {
        paths: [
            'betting/:locale/apps/virtual',
            'betting/:locale/apps/virtual/:sport',
            'betting/:locale/apps/virtual/:sport/:subcategory',
        ],
        action: function (locale, sport, subcategory) {
            changePage(pages.getVirtual, {
                locale: locale,
                sport: sport,
                page: 'virtual',
                subcategory: subcategory,
            });
        },
    },
};

const schedulePage = {
    schedule: {
        paths: [
            'betting/:locale/apps/schedule/in-play(/sport/:sport)(/date/:date)(/streaming)',
        ],
        action: function (locale, sport, date) {
            changePage(pages.getSchedule, {
                locale: locale || 'en-gb',
                sport: (sport) ? sport : 'all-sports',
                date: (date) ? date : moment().locale('en-gb').format('dddd').toLowerCase(),
                streaming: getLocationPathname().includes('streaming') ? 'streaming-only' : '',
                page: 'schedule',
            });
        },
    },
};

const resultsPage = {
    results: {
        paths: [
            'betting/:locale/apps/results',
            'betting/:locale/apps/results/:sport',
            'betting/:locale/apps/results/:sport/:eventId',
        ],
        action: function (locale, sport, eventId) {
            changePage(pages.getResults, {
                locale: locale,
                sport: (sport) ? sport : 'all',
                eventId: eventId,
                page: 'results',
            });
        },
    },
};

const totePage = {
    tote: {
        paths: [
            'betting/:locale/:sport/tote',
        ],
        action: function (locale, sport) {
            changePage(pages.getTote, {
                locale: locale,
                sport: sport,
                page: 'tote',
            });
        },
    },
};

const internationalRacingPage = {
    internationalRacing: {
        paths: [
            'betting/:locale/:sport/international-pool-betting',
        ],
        action: function (locale, sport) {
            changePage(pages.getInternationalRacing, {
                locale: locale,
                sport: sport,
                page: 'internationalRacing',
            });
        },
    },
};

const tournamentsPage = {
    tournaments: {
        paths: [
            'betting/:locale/:sport/tournaments/:tournamentName(/*trailingPath)',
        ],
        action: function (locale, sport, tournamentName, tournamentContext) {
            if (
                !currentPage || currentPage.id !== 'tournaments' || currentPage.args.tournamentName !== tournamentName
            ) {
                changePage(pages.getTournaments, {
                    page: 'tournaments',
                    locale,
                    sport,
                    tournamentName,
                    tournamentContext,
                });
            }
        },
    },
};

const jockeysTrainersPage = {
    jockeysTrainers: {
        paths: [
            'betting/:locale/:sport/jockeys-trainers',
        ],
        action: function (locale, sport) {
            changePage(pages.getJockeysTrainers, {
                locale: locale,
                sport: sport,
                page: 'jockeysTrainers',
            });
        },
    },
};

const napsPage = {
    naps: {
        paths: [
            'betting/:locale/:sport/naps',
        ],
        action: function (locale, sport) {
            changePage(pages.getNaps, {
                locale: locale,
                sport: sport,
                page: 'naps',
            });
        },
    },
};

const inPlayPage = {
    inPlay: {
        paths: [
            'betting/:locale/in-play/all',
            'betting/:locale/in-play/all/:streamed',
            'betting/:locale/:sport/in-play',
            'betting/:locale/in-play/:sport',
            'betting/:locale/in-play/:sport/:streamed',
        ],
        action: function (locale, sport, streamed) {
            changePage(pages.getInPlay, {
                locale: locale,
                sport: (!_.isUndefined(sport) && (sport !== 'all')) ? sport : 'in-play',
                page: 'inPlay',
                streamed: streamed === 'streamed',
            });
        },
    },
};

const staticSEOPagePage = {
    staticSEO: {
        paths: [
            'betting/:locale/pages/:slug',
        ],
        action: function (locale, slug) {
            changePage(pages.getStaticSEO, {
                locale: locale,
                slug: slug,
                page: 'staticSEO',
                sport: 'staticSEO',
            });
        },
    },
};

const homePage = {
    home: {
        paths: [
            'betting/:locale',
        ],
        action: function (locale) {
            changePage(pages.getHome, {
                locale: locale,
                sport: 'homepage',
                page: 'home',
            });
        },
    },
};

const couponsPage = {
    coupons: {
        paths: [
            'betting/:locale/:sport/coupons',
            'betting/:locale/:sport/coupons/:couponId',
            'betting/:locale/:sport/coupons/:sortOrder/:couponId',
        ],
        action: function (locale, sport, sortOrder, couponId) {
            sortOrder = cleanUpTestingParameters(sortOrder);
            couponId = cleanUpTestingParameters(couponId);

            changePage(pages.getCoupons, {
                locale: locale,
                sport: sport,
                page: 'coupons',
                sortOrder: sortOrder ? sortOrder : 'competition',
                couponId: couponId,
            });
        },
    },
};

const promotionsPage = {
    promotions: {
        paths: [
            'betting/:locale/apps/promotions',
            'betting/:locale/apps/promotions/offer/:slugUrl',
        ],
        action: function (locale, slugUrl) {
            changePage(pages.getPromotions, {
                locale: locale,
                sport: 'promotions',
                page: 'promotions',
                slugUrl: slugUrl,
            });
        },
    },
};

const extraPages = {
    rewards: {
        paths: [
            'betting/:locale/apps/rewards',
        ],
        action: function (locale) {
            changePage(pages.getRewards, {
                locale: locale,
                sport: 'top-bets-home',
                page: 'rewards',
            });
        },
    },
    lotteries: {
        paths: [
            'betting/:locale/apps/lotteries',
        ],
        action: function (locale) {
            changePage(pages.getLotteries, {
                locale: locale,
                sport: 'lotteries',
                page: 'lotteries',
            });
        },
    },
    topbets: {
        paths: [
            'betting/:locale/:sport/top-bets',
        ],
        action: function (locale, sport) {
            const targetSport = sport === 'apps' ? 'top-bets-home' : sport;

            changePage(pages.getTopBets, {
                locale: locale,
                sport: targetSport,
                page: 'topbets',
            });
        },
    },
    highlights: {
        paths: [
            'betting/:locale/:sport',
        ],
        action: function (locale, sport) {
            changePage(pages.getHighlights, {
                locale: locale,
                sport: sport,
                page: 'highlights',
            });
        },
    },
    event: {
        paths: [
            'betting/:locale/:sport/OB_EV:eventId(/*trailingPath)',
        ],
        action: function (locale, sport, eventId, trailingPath) {
            function getEventPage(sport) {
                const page = window.WH.sportsbook.page;
                if (['greyhounds', 'horse-racing'].includes(sport)) {
                    return (['antePost', 'specials'].includes(page)) ? page : 'meetings';
                }
                return 'events';
            }

            changePage(pages.getEventsMulti, {
                locale: locale,
                sport: sport,
                eventId: eventId,
                raceId: eventId,
                page: getEventPage(sport),
                inPlay: ('' + trailingPath).endsWith('inplay'),
            });
        },
    },
};

const pagesRoutes = Object.assign({},
    fallbackPage,
    tournamentsPage,
    competitionsPage,
    matchesPage,
    racingPage,
    meetingsPage,
    antePostPage,
    specialsPage,
    inPlayPage,
    homePage,
    couponsPage,
    promotionsPage,
    staticSEOPagePage,
    totePage,
    jockeysTrainersPage,
    napsPage,
    internationalRacingPage,
    extraPages,
    virtualPage,
    schedulePage,
    resultsPage
);

export default function setupRoutes({ router, overwriteCurrentPage, ...dependencies }) {
    deps = dependencies;

    if (overwriteCurrentPage) { // this is used for tests
        currentPage = overwriteCurrentPage;
    }

    _.each(pagesRoutes, ({ paths, action }, route) => {
        _.each(paths, path => router.route(path, route));
        router.on(`route:${route}`, action);
    });
}
